import { memo, useState } from "react";
import { TextField } from "@mui/material";
import { Dropdown } from "semantic-ui-react";
import UCUM from "../dictionaries/UCUM";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const displayStringFromFHIRQuantity = (quantity) => {
  if (quantity === undefined || quantity === null || quantity === '') {
    return ""
  }
  if (typeof quantity === "object") {
    try {
      let quantityDisplay = "";
      if (quantity.comparator) {
        quantityDisplay = quantity.comparator + " ";
      }
      let value = quantity.value;
      if (value != value.toFixed(3)) {
        value = value.toFixed(3);
      }
      if (value !== undefined && value !== null) {
        quantityDisplay += value.toString();
      }
      if (quantity.unit || quantity.code) {
        if (quantityDisplay) {
          quantityDisplay += " " + (quantity.unit || quantity.code);
        } else {
          quantityDisplay = quantity.unit || quantity.code;
        }
      }
      if (quantityDisplay && typeof quantityDisplay === "string") {
        return quantityDisplay;
      } else if (quantityDisplay === "") {
        return ""
      }
    } catch (e) { 
      console.log("ERROR for displayStringFromFHIRQuantity for display of:");
      console.log(quantity);
      console.log(e);
     }
  }
  if (typeof quantity === "string") {
    return `${quantity} [ERROR: Quantity data not in expected Quantity datatype.]`
  }
  return "[ERROR: Quantity data not interpretable.]";
}

const alertIfInvalidNumberEntry = (e) => {
  if (e.nativeEvent.data === null || e.nativeEvent.data === undefined) {
    //
  } else if (e.nativeEvent.data.length === 1 && /^[0-9.,+-]*$/.test(e.nativeEvent.data)) {
    //
  } else if (e.nativeEvent.data.length > 1 && /^-?[0-9]\d*(\.\d+)*(,\d+)?$/.test(e.nativeEvent.data)) {
    //
  } else {
    alert('You entered data with characters unsuitable for number data entry (e.g. space, letter, or unrecognized character). Data will not be retained on saving.')
  }
};

const quantityDotComparatorOptions = [{ "key": "0", "value": "=", "text": "=" }, { "key": "1", "value": "<", "text": "<" }, { "key": "2", "value": "<=", "text": "<=" }, { "key": "3", "value": ">", "text": ">" }, { "key": "4", "value": ">=", "text": ">=" }, { "key": "5", "value": "ad", "text": "ad" }]
const QuantityEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState,
  dataEntryStyle, valueSet, inTableCell, startCollapsed }) => {
  if (!startingValue) {
    startingValue = "";
  }
  if (dataEntryStyle === "Age") {
    valueSet = UCUM.age;
  } else if (dataEntryStyle === "Duration") {
    valueSet = UCUM.duration;
  } else if (valueSet === "UCUM") {
    valueSet = UCUM.common;
  }

  const [quantityPresetState, setQuantityPresetState] = useState({ edited: false });
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  if (startCollapsedState) {
    return <>
      <p>
        <b>{fieldLabel}: </b>
        {startingValue && displayStringFromFHIRQuantity(startingValue)}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </p>
    </>
  } else {
    return <><p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      {dataEntryStyle !== "SimpleQuantity" &&
        <Dropdown className="dropdownWithClearIconUpperRight" name="comparator" placeholder="Comparator"
          closeOnChange selection clearable selectOnBlur={false} autoComplete="off"
          style={{ minWidth: "18%", width: "18%" }}
          options={quantityDotComparatorOptions}
          value={startingValue.comparator || ""}
          onChange={(e, data) => {
            let newQuantity = { comparator: "", value: "", unit: "" };
            newQuantity.comparator = data.value;
            if (startingValue.value) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            if (startingValue.unit) { newQuantity.unit = startingValue.unit; } else { delete newQuantity.unit; }
            if (startingValue.system) { newQuantity.system = startingValue.system; }
            if (startingValue.code) { newQuantity.code = startingValue.code; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }}
        />
      }
      &nbsp;&nbsp;&nbsp;
      <TextField style={{ width: "15%" }} className="inputField" type='number' inputProps={{ step: "any" }} label={'Value'}
        size="small" variant='outlined' value={(startingValue.value || startingValue.value === 0) ? startingValue.value : ''}
        onInput={alertIfInvalidNumberEntry}
        onChange={(e) => {
          let newQuantity = { comparator: "", value: "", unit: "" };
          if (e.target.value === "" || e.target.value === null || e.target.value === undefined) {
            newQuantity.value = "";
          } else if (isNaN(e.target.value)) {
            newQuantity.value = e.target.value;
          } else {
            let numberInput = parseFloat(e.target.value);
            if (dataEntryStyle === "Age") {
              if (numberInput < 0) {
                numberInput = -1 * numberInput;
              }
              if (numberInput !== parseFloat(e.target.value)) {
                alert("Only positive numbers are allowed. Value changed to: " + numberInput);
              }
            }
            newQuantity.value = numberInput;
          }
          if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
          if (startingValue.unit) { newQuantity.unit = startingValue.unit; } else { delete newQuantity.unit; }
          if (startingValue.system) { newQuantity.system = startingValue.system; }
          if (startingValue.code) { newQuantity.code = startingValue.code; }
          if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
            setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
          } else {
            setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
          }
        }} />
      &nbsp;&nbsp;&nbsp;
      {inTableCell && <br />}
      {(dataEntryStyle === "Age" || dataEntryStyle === "Duration") &&
        <Dropdown name="timeunit" placeholder="Units of Time"
          closeOnChange selection selectOnBlur={false} autoComplete="off"
          style={{ minWidth: "22%", width: "22%" }}
          options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
          value={startingValue.code || ""}
          onChange={(e, data) => {
            let selectedCode;
            let selectedSystem;
            let selectedDisplay;
            for (const term of valueSet) {
              if (term.code === data.value) {
                selectedCode = term.code;
                selectedSystem = term.system;
                selectedDisplay = term.display;
              }
            }
            let newQuantity = { comparator: "", value: "", unit: selectedDisplay, system: selectedSystem, code: selectedCode };
            if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
            if (startingValue.value || startingValue.value === 0) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }}
        />
      }
      {dataEntryStyle !== "Age" && dataEntryStyle !== "Duration" && !valueSet &&
        <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Units'}
          size="small" variant='outlined' value={startingValue.unit || ''}
          onChange={(e) => {
            let newQuantity = { comparator: "", value: "", unit: "" };
            if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
            if (startingValue.value || startingValue.value === 0) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            newQuantity.unit = e.target.value;
            if (startingValue.system) { newQuantity.system = startingValue.system; }
            if (startingValue.code) { newQuantity.code = startingValue.code; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }} />
      }
      {dataEntryStyle !== "Age" && dataEntryStyle !== "Duration" && valueSet && <>
        <Dropdown
          name={"unit-picklist-for-" + elementName}
          placeholder="Select Units" search={true}
          closeOnChange selection clearable selectOnBlur={false} autoComplete="off"
          style={{ minWidth: "25%", width: "25%" }}
          options={valueSet.map((term, termIndex) => { return { "key": termIndex, "value": term.code, "text": term.display } })}
          value={quantityPresetState.edited ? "" : startingValue.code}
          onChange={(e, data) => {
            let selectedCode;
            let selectedSystem;
            let selectedDisplay;
            for (const term of valueSet) {
              if (term.code === data.value) {
                selectedCode = term.code;
                selectedSystem = term.system || "";
                selectedDisplay = term.display || "";
              }
            }
            let newQuantity = { comparator: "", value: "", unit: selectedDisplay, system: selectedSystem, code: selectedCode };
            if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
            if (startingValue.value || startingValue.value === 0) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
          }}
        />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "25%" }} className="inputField" type='text' label={'System'}
          size="small" variant='outlined' value={startingValue.system || ''}
          onChange={(e) => {
            let newQuantity = { comparator: "", value: "", unit: "", system: "", code: "" };
            if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
            if (startingValue.value || startingValue.value === 0) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            newQuantity.system = e.target.value;
            if (startingValue.unit) { newQuantity.unit = startingValue.unit; } else { delete newQuantity.unit; }
            if (startingValue.code) { newQuantity.code = startingValue.code; } else { delete newQuantity.code; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
            setQuantityPresetState({ edited: true });
          }} />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Code'}
          size="small" variant='outlined' value={startingValue.code || ''}
          onChange={(e) => {
            let newQuantity = { comparator: "", value: "", unit: "", system: "", code: "" };
            if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
            if (startingValue.value || startingValue.value === 0) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            newQuantity.code = e.target.value;
            if (startingValue.unit) { newQuantity.unit = startingValue.unit; } else { delete newQuantity.unit; }
            if (startingValue.system) { newQuantity.system = startingValue.system; } else { delete newQuantity.system; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
            setQuantityPresetState({ edited: true });
          }} />
        &nbsp;&nbsp;&nbsp;
        <TextField style={{ width: "25%" }} className="inputField" type='text' label={'Unit Display'}
          size="small" variant='outlined' value={startingValue.unit || ''}
          onChange={(e) => {
            let newQuantity = { comparator: "", value: "", unit: "", system: "", code: "" };
            if (startingValue.comparator) { newQuantity.comparator = startingValue.comparator; } else { delete newQuantity.comparator; }
            if (startingValue.value || startingValue.value === 0) { newQuantity.value = startingValue.value; } else { delete newQuantity.value; }
            newQuantity.unit = e.target.value;
            if (startingValue.code) { newQuantity.code = startingValue.code; } else { delete newQuantity.code; }
            if (startingValue.system) { newQuantity.system = startingValue.system; } else { delete newQuantity.system; }
            if (newQuantity.comparator || newQuantity.value || newQuantity.value === 0 || newQuantity.unit || newQuantity.system || newQuantity.code) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newQuantity } });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} } });
            }
            setQuantityPresetState({ edited: true });
          }} />
      </>
      }
    </>
  }
});

export default QuantityEntry;
export { alertIfInvalidNumberEntry, displayStringFromFHIRQuantity };